import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { SALE_BULK_UPDATE_FULFILLER_UPDATE_KEY } from 'src/components/Purchases/PurchaseActionDropdown';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { PosSpinner } from 'src/core/POS/PosSpinner/PosSpinner';
import { vars } from 'src/core/themes';
import { Colors } from 'src/core/themes/constants/colors';
import { Stack } from 'src/core/ui';
import { BulkUpdateSalesFulfillerDialog } from 'src/dialogs/BulkEdits/BulkUpdateSalesFulfillerDialog';
import { useGetFulfillmentProviderSettings } from 'src/hooks/useGetFulfillmentProviderSettings';
import { DeleteIcon, EditIcon, IconsFill, MoreIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditPreviewWithDetails,
  BulkEditSaleClient,
  SellerAccountFulfillmentRulesClient,
} from 'src/WebApiController';

import * as styles from '../FulfillmentProviders.css';

export interface GroupedFulfillmentRule {
  fulfillerUserId: string;
  fulfillerName: string; // Resolved fulfillment provider name
  venueIds: number[];
  venueNames: string[]; // We'll resolve venue names
  ruleIds: number[]; // Store individual rule IDs for editing/deleting
}

export interface FulfillmentRulesProps {
  onEditRuleGroup: (group: GroupedFulfillmentRule) => void;
}
export const FulfillmentRules: React.FC<FulfillmentRulesProps> = ({
  onEditRuleGroup,
}) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const queryClient = useQueryClient();
  const [isDeletingRuleIds, setIsDeletingRuleIds] = useState<number[]>([]);

  const actionsText = useContent(ContentId.Actions);
  const updateKey = SALE_BULK_UPDATE_FULFILLER_UPDATE_KEY;
  const { openDialog, closeDialog } = useDialog(
    DialogId.BulkBackfillSalesFulfiller,
    BulkUpdateSalesFulfillerDialog
  );

  // Fetch venue-level fulfillment rules
  const {
    data: fulfillmentRules,
    isLoading: isLoadingRules,
    error: rulesError,
  } = useQuery({
    queryKey: [
      'VenueLevelFulfillmentRules',
      activeAccountWebClientConfig.activeAccountId,
    ],
    queryFn: async () => {
      const client = new SellerAccountFulfillmentRulesClient(
        activeAccountWebClientConfig
      );
      return await client.getVenueLevelFulfillmentRules();
    },
    enabled: activeAccountWebClientConfig.activeAccountId != null,
    refetchOnWindowFocus: false,
  });

  // Fetch fulfillment provider settings to map IDs to names
  const { fulfillmentProviderSettings, fulfillmentProviderSettingsQuery } =
    useGetFulfillmentProviderSettings();

  // Create fulfillment provider lookup map
  const fulfillmentProviderMap = useMemo(() => {
    if (!fulfillmentProviderSettings) return new Map<string, string>();

    return new Map(
      fulfillmentProviderSettings.map((provider) => [
        provider.fulfillmentProviderSellerUserId,
        provider.fulfillmentProviderName,
      ])
    );
  }, [fulfillmentProviderSettings]);

  // Group fulfillment rules by fulfiller user ID
  const groupedRules = useMemo((): GroupedFulfillmentRule[] => {
    if (!fulfillmentRules) return [];

    const groups = new Map<string, GroupedFulfillmentRule>();

    fulfillmentRules.forEach((rule) => {
      if (rule.viagogoVenueId === null) return;

      const key = rule.fulfillerSellerUserId;
      if (!groups.has(key)) {
        const providerName =
          fulfillmentProviderMap.get(rule.fulfillerSellerUserId) ||
          rule.fulfillerSellerUserId;

        groups.set(key, {
          fulfillerUserId: rule.fulfillerSellerUserId,
          fulfillerName: providerName,
          venueIds: [],
          venueNames: [],
          ruleIds: [],
        });
      }

      const group = groups.get(key)!;
      group.venueIds.push(rule.viagogoVenueId);
      group.venueNames.push(rule.venueName ?? '');
      group.ruleIds.push(rule.sellerAccountFulfillmentRuleId);
    });

    return Array.from(groups.values());
  }, [fulfillmentRules, fulfillmentProviderMap]);

  const handleEdit = (group: GroupedFulfillmentRule) => {
    onEditRuleGroup(group);
  };

  const handleDelete = async (ruleIds: number[]) => {
    setIsDeletingRuleIds(ruleIds);
    try {
      const client = new SellerAccountFulfillmentRulesClient(
        activeAccountWebClientConfig
      );
      await client.deleteFulfillmentRules(ruleIds);
      await queryClient.invalidateQueries({
        queryKey: [
          'VenueLevelFulfillmentRules',
          activeAccountWebClientConfig.activeAccountId,
        ],
      });
    } catch (error) {
      showErrorDialog(
        'deleteFulfillmentRules',
        { message: 'Failed to delete fulfillment rules' },
        {
          trackErrorData: { ruleIds: ruleIds },
        }
      );
    } finally {
      setIsDeletingRuleIds([]);
    }
  };

  const handleApplyToExisting = useCallback(
    (group: GroupedFulfillmentRule) => {
      const onBulkUpdate = async (
        supportBackgroundProcess?: boolean,
        onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
        preview?: BulkEditPreviewWithDetails
      ) => {
        if (onPreviewReceived) {
          await tryInvokeApi(
            async () => {
              const preview = await new BulkEditSaleClient(
                activeAccountWebClientConfig
              ).bulkBackfillSalesFulfillerPreview({
                eventId: null,
                venueIds: group.venueIds,
                fulfillerSellerUserId: group.fulfillerUserId,
              });

              onPreviewReceived(preview);
            },
            (error) => {
              showErrorDialog('bulkBackfillSalesFulfillerPreview', error, {
                trackErrorData: {
                  fulfillerUserId: group.fulfillerUserId,
                  venueIds: group.venueIds,
                },
              });
            }
          );
        } else {
          await tryInvokeApi(
            async () => {
              const succeeded = await new BulkEditSaleClient(
                activeAccountWebClientConfig
              ).bulkBackfillSalesFulfiller(
                {
                  item1: preview!.preview,
                  item2: group.fulfillerUserId,
                },
                updateKey,
                supportBackgroundProcess
              );
              if (!supportBackgroundProcess) {
                closeDialog();
              }
            },
            (error) => {
              showErrorDialog('bulkBackfillSalesFulfiller', error, {
                trackErrorData: {
                  preview,
                },
              });
            }
          );
        }
      };

      openDialog({
        updateKey: updateKey,
        entityType: ActionOutboxEntityType.Sale,
        onSaveOverrideSettings: () => {}, // No additional save action needed for existing rules
        onBulkUpdate: onBulkUpdate,
        onCancel: () => {
          closeDialog();
        },
      });
    },
    [
      activeAccountWebClientConfig,
      closeDialog,
      openDialog,
      showErrorDialog,
      updateKey,
    ]
  );

  if (isLoadingRules || fulfillmentProviderSettingsQuery.isLoading) {
    return (
      <div>
        <Content id={ContentId.LoadingFulfillmentRules} />
      </div>
    );
  }

  if (rulesError) {
    return (
      <div>
        <Content id={ContentId.ErrorLoadingFulfillmentRules} />{' '}
        {rulesError.message}
      </div>
    );
  }

  if (!groupedRules.length) {
    return (
      <div>
        <Content id={ContentId.NoFulfillmentRulesFound} />
      </div>
    );
  }

  return (
    <div>
      {groupedRules.map((rule, index) => {
        const isDeleting = rule.ruleIds.some((ruleId) =>
          isDeletingRuleIds.includes(ruleId)
        );

        return (
          <div
            key={`${rule.fulfillerUserId}-${index}`}
            className={styles.ruleCard}
            style={{ position: 'relative' }}
          >
            {isDeleting && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10,
                }}
              >
                <PosSpinner size="24px" />
              </div>
            )}
            <div style={{ opacity: isDeleting ? 0.5 : 1 }}>
              <div className={styles.ruleActions}>
                <PosDropdown
                  trigger={
                    <div title={actionsText}>
                      <MoreIcon
                        withHoverEffect
                        fill={IconsFill.textBrand}
                        size={vars.iconSize.m}
                      />
                    </div>
                  }
                  align="end"
                  triggerProps={{ disabled: isDeleting }}
                >
                  <PosDropdownItem
                    onClick={() => handleEdit(rule)}
                    disabled={isDeleting}
                  >
                    <Stack alignItems="center" gap="m">
                      <EditIcon size={vars.iconSize.m} />
                      <Content id={ContentId.Edit} />
                    </Stack>
                  </PosDropdownItem>
                  <PosDropdownItem
                    onClick={() => handleDelete(rule.ruleIds)}
                    disabled={isDeleting}
                  >
                    <Stack alignItems="center" gap="m">
                      <DeleteIcon size={vars.iconSize.m} />
                      <Content id={ContentId.Remove} />
                    </Stack>
                  </PosDropdownItem>
                  <PosDropdownItem
                    onClick={() => handleApplyToExisting(rule)}
                    disabled={isDeleting}
                  >
                    <Content id={ContentId.ApplyRuleToExistingInvoices} />
                  </PosDropdownItem>
                </PosDropdown>
              </div>

              <div className={styles.ruleRow}>
                <div className={styles.ruleLabel}>
                  <Content id={ContentId.FulfilledBy} />:
                </div>
                <div className={styles.ruleValue}>{rule.fulfillerName}</div>
              </div>

              {rule.venueNames.length > 0 && (
                <div className={styles.ruleRow}>
                  <div className={styles.ruleLabel}>
                    <Content id={ContentId.Venues} />:
                  </div>
                  <div className={styles.ruleValue}>
                    <div className={styles.venueTagsContainer}>
                      {rule.venueNames.slice(0, 20).map((venueName, index) => (
                        <span
                          key={`${rule.venueIds[index]}-${index}`}
                          className={styles.venueTag}
                        >
                          {venueName}
                        </span>
                      ))}
                      {rule.venueNames.length > 20 && (
                        <span
                          className={styles.venueTag}
                          style={{
                            color: Colors.gray600,
                            fontStyle: 'italic',
                          }}
                        >
                          <FormatContent
                            id={FormatContentId.AndXMore}
                            params={`${rule.venueNames.length - 20}`}
                          />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
